import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useCourseManifest } from "@/lib/course";
import { useProgress } from "@/lib/progress";
import { isLessonUnlocked } from "@/lib/navigation";
import { AppTopbar } from "@/components/AppTopbar";
import { CourseOutlineDrawer } from "@/components/CourseOutlineDrawer";
import { DraftChannelBanner } from "@/components/DraftChannelBanner";
import { useChannel, withChannel } from "@/lib/channel";

export default function CourseOutline() {
  const { courseSlug: courseSlugParam } = useParams<{ courseSlug: string }>();
  const courseSlug = courseSlugParam ?? "";
  const manifest = useCourseManifest(courseSlug);
  const channel = useChannel();
  const progress = useProgress(manifest.status === "ready" ? manifest.manifest.slug : "", channel);
  const [outlineOpen, setOutlineOpen] = useState(false);

  const progRec = progress.status === "ready" ? progress.progress : null;

  // Gate: if onboarding is required and not complete, redirect to onboarding
  if (manifest.status === "ready") {
    if (manifest.manifest.requireOnboarding === true && manifest.manifest.onboardingComplete !== true) {
      return <Navigate to={withChannel(`/courses/${courseSlug}/onboarding`, channel)} replace />;
    }
  }

  return (
    <>
      <AppTopbar
        {...(manifest.status === "ready" ? { manifest: manifest.manifest, progress: progRec } : {})}
        onOpenDrawer={() => setOutlineOpen(true)}
      />
      {manifest.status === "ready" && (
        <CourseOutlineDrawer
          open={outlineOpen}
          onClose={() => setOutlineOpen(false)}
          manifest={manifest.manifest}
          progress={progRec}
          courseSlug={courseSlug}
          currentChapterFolder=""
          currentLessonSlug=""
        />
      )}
      <main className="p-8 max-w-4xl mx-auto">
        {manifest.status === "loading" && <p className="text-muted-foreground">Loading…</p>}
        {manifest.status === "error" && (
          <p className="text-destructive">Failed to load: {manifest.message}</p>
        )}
        {manifest.status === "ready" && (
          <>
            <DraftChannelBanner />
            <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mb-1">{manifest.manifest.title}</h1>
            <p className="text-muted-foreground text-sm mb-8">{manifest.manifest.summary}</p>

            <ol className="space-y-6">
              {manifest.manifest.chapters.map((chapter) => (
                <li
                  key={chapter.folder}
                  className="rounded-card border border-border bg-card/50 p-5"
                >
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="inline-block rounded bg-primary/20 text-primary-soft px-2 py-0.5 text-xs font-semibold uppercase tracking-wider">
                      Ch {chapter.chapterNumber}
                    </span>
                    <h2 className="text-lg font-semibold">{chapter.title}</h2>
                    <span className="text-xs text-muted-foreground ml-auto uppercase">
                      {chapter.environment}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm mb-3">{chapter.summary}</p>
                  {chapter.lessons.length > 0 ? (
                    <ul className="space-y-1">
                      {chapter.lessons.map((lesson, idx) => {
                        const unlocked = isLessonUnlocked(
                          manifest.manifest,
                          progRec,
                          chapter.folder,
                          lesson.slug
                        );
                        if (!unlocked) {
                          return (
                            <li key={lesson.slug}>
                              <span className="block rounded px-3 py-2 text-sm text-muted-foreground select-none">
                                <span className="text-subtle-foreground mr-2">{idx + 1}.</span>
                                {lesson.title}
                                <span className="text-subtle-foreground text-xs ml-2">
                                  · {lesson.estimatedMinutes}m
                                </span>
                              </span>
                            </li>
                          );
                        }
                        return (
                          <li key={lesson.slug}>
                            <Link
                              to={withChannel(`/courses/${courseSlug}/${chapter.folder}/${lesson.slug}`, channel)}
                              className="block rounded px-3 py-2 hover:bg-muted/50 text-sm text-foreground"
                            >
                              <span className="text-muted-foreground mr-2">{idx + 1}.</span>
                              {lesson.title}
                              <span className="text-muted-foreground text-xs ml-2">
                                · {lesson.estimatedMinutes}m
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground text-sm italic">No lessons yet</p>
                  )}
                </li>
              ))}
            </ol>
          </>
        )}
      </main>
    </>
  );
}
